import { HttpResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { InputGala } from '@composants/index';
import { UtilisateurListe, Utilisateur } from '@estel-numerique/galaclick-api';
import { TranslateService } from '@ngx-translate/core';
import { IncarnationService } from '@services/incarnation.service';
import { MonitoringService } from '@services/monitoring.service';
import { UtilService } from '@services/Util.service';
import { lastValueFrom } from 'rxjs';
import { DialogGalaComponent } from '../composants/dialog-gala/dialog-gala.component';

/**
 * Modale de sélection de l'utilisateur sous lequel se connecter, ouverte depuis le menu
 * pour les comptes dont le jeton de connexion porte le rôle Keycloak "superviseur"
 * (`Utilisateur.ROLESUP`) - voir `menu-gala.component.ts`. La liste (utilisateurs
 * incarnables de l'organisme) est établie et contrôlée côté WS.
 */
@Component({
    templateUrl: './incarnation.component.html',
    styleUrls: ['./incarnation.component.css'],
    imports: [DialogGalaComponent, FormsModule, InputGala, MatButton, MatProgressSpinner]
})
export class IncarnationComponent implements OnInit {
    protected readonly util = inject(UtilService);
    protected readonly translate = inject(TranslateService);
    private readonly monitoServ = inject(MonitoringService);
    private readonly dialogRef = inject(MatDialogRef<IncarnationComponent>);
    private readonly incarnationServ = inject(IncarnationService);

    protected readonly utilisateurs = signal<UtilisateurListe[]>([]);
    protected readonly recherche = signal('');
    protected readonly chargement = signal(true);
    protected readonly enCours = signal(false);
    protected readonly selection = signal<UtilisateurListe | null>(null);

    constructor() {
        this.dialogRef.updateSize('600px');
    }

    protected readonly utilisateursFiltres = computed(() => {
        const terme = this.recherche().trim().toUpperCase();
        if (!terme) {
            return this.utilisateurs();
        }
        return this.utilisateurs().filter(u => u.LOGIN?.toUpperCase().includes(terme)
            || u.NOM?.toUpperCase().includes(terme) || u.PRENOM?.toUpperCase().includes(terme));
    });

    ngOnInit(): void {
        this.chargerUtilisateurs().catch(e => this.util.fErreur(e));
    }

    private async chargerUtilisateurs() {
        this.chargement.set(true);
        try {
            this.utilisateurs.set(await lastValueFrom(this.incarnationServ.lister()));
        } finally {
            this.chargement.set(false);
        }
    }

    protected selectionner(utilisateur: UtilisateurListe) {
        this.selection.set(utilisateur);
    }

    protected retour() {
        this.dialogRef.close();
    }

    /** Demande confirmation puis ouvre la session de l'utilisateur sélectionné */
    protected seConnecter() {
        const cible = this.selection();
        if (this.enCours() || cible?.USRID == null) {
            return;
        }
        const idutilisateur = cible.USRID;
        this.util.fConfirm(
            this.translate.instant('INCARNATION.CONFIRMATION', { libelle: `${cible.PRENOM} ${cible.NOM}` }),
            () => {
                this.lancerIncarnation(idutilisateur).catch(e => this.util.fErreur(e));
            });
    }

    private async lancerIncarnation(idutilisateur: number) {
        this.enCours.set(true);
        try {
            this.ouvrirSession(await lastValueFrom(this.incarnationServ.incarner(idutilisateur)));
        } finally {
            this.enCours.set(false);
        }
    }

    /** Ouvre la session de l'utilisateur incarné (même logique que la connexion classique, voir AuthComponent) */
    private ouvrirSession(resp: HttpResponse<Utilisateur>) {
        sessionStorage.setItem("logged", JSON.stringify(true));
        this.util.updateToken(resp);
        const data = resp.body;
        sessionStorage.setItem("user", JSON.stringify(data));
        this.monitoServ.monitorUser(data);
        this.util.superAdmin = false;
        this.util.fRefreshMenuItem();
        this.dialogRef.close();
        // Rechargement complet sur l'accueil : l'URL est changée sans passer par le routeur pour n'afficher la page qu'une fois
        history.replaceState(null, '', '#/');
        location.reload();
    }
}
